/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-12-14 09:30:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Grid } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Filter from 'src/components/containers/analytics-v2/filter';
import Chart from 'src/components/views/analytics-v2/display/chart';
import AnalyticsChartImpact from 'src/components/views/analytics-v2/display/chart-impact';

import { formatNumberInThousand } from 'src/helpers';
import { getUTCDate } from 'src/helpers/date-time-helper';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import {
  buildCompareDateRange,
  buildDateRangeFromDate,
  displayDateRangeV2
} from 'src/helpers/analytics-helpers';
import {
  QUERY_EXECUTION_KEY,
  FEATURES,
  MAX_NUM_X_AXIS_POINTS_APP_IMPACT_CHART,
  limitXAxisPoint
} from 'src/constants/analytics';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import { enableAnalyticV2Recommendation } from 'src/helpers/analytics-v2-helpers';
import WarningMetricIncompatible from 'src/components/views/analytics-v2/warning-metric-incompatible';
import { formatMoney } from 'src/helpers/money-format';
import { onAbortApiAnalytic } from 'src/helpers/analytics-v2-helpers';

type Props = {
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;
type State = {};

const fromDateStateName = 'appImpactFromDate';
const toDateStateName = 'appImpactToDate';
const compareStateName = 'appImpactCompare';

class AppImpactContainer extends Component<ComposedProps, State> {
  state = {};

  componentDidMount() {
    const { fromDate, toDate, compare } = this.props;
    if (fromDate && toDate) {
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);

      this.fetchAll(fromDateUTC, toDateUTC, compare);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { fromDate, toDate, compare } = this.props;
    const { fromDate: nextFromDate, toDate: nextToDate, compare: nextCompare } = nextProps;

    // Check changing filter
    if (fromDate !== nextFromDate || toDate !== nextToDate || compare !== nextCompare) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(nextFromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(nextToDate, 24, 0, 0, 0);

      onAbortApiAnalytic();
      setTimeout(() => {
        this.fetchAll(fromDateUTC, toDateUTC, nextCompare);
      }, 500);
    }

    // Update execution ids
    this.processUpdateExecutionId(this.props, nextProps);
  }

  processHideLandMarks = categories => {
    const landmarksTotalRevenue = document.querySelectorAll(
      '.AppImpact-Total-revenue .apexcharts-xaxis-tick'
    );
    landmarksTotalRevenue.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksConversionRate = document.querySelectorAll(
      '.AppImpact-Conversion-rate .apexcharts-xaxis-tick'
    );
    landmarksConversionRate.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksOrderCount = document.querySelectorAll(
      '.AppImpact-Order-count .apexcharts-xaxis-tick'
    );
    landmarksOrderCount.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksAvgOrderValue = document.querySelectorAll(
      '.AppImpact-Avg-order-value .apexcharts-xaxis-tick'
    );
    landmarksAvgOrderValue.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksRevenuePerVisitor = document.querySelectorAll(
      '.AppImpact-Revenue-per-visitor .apexcharts-xaxis-tick'
    );
    landmarksRevenuePerVisitor.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
  };

  render() {
    const {
      fromDate,
      toDate,
      compare,

      totalRevenueData,
      totalRevenueCompared,
      totalRevenueTotal,
      totalRevenuePercentChange,
      totalRevenueLoading,

      totalConversionRateData,
      totalConversionRateCompared,
      totalConversionRateTotal,
      totalConversionRatePercentChange,
      totalConversionRateLoading,

      orderCountData,
      orderCountCompared,
      orderCountTotal,
      orderCountPercentChange,
      orderCountLoading,

      averageOrderValueData,
      averageOrderValueCompared,
      averageOrderValueTotal,
      averageOrderValuePercentChange,
      averageOrderValueLoading,

      revenuePerVisitorData,
      revenuePerVisitorCompared,
      revenuePerVisitorTotal,
      revenuePerVisitorPercentChange,
      revenuePerVisitorLoading,

      openReviewAddonModal,
      performActionSurvey,
      tenantApp,
      moneyFormat,
      i18n
    } = this.props;
    const isEnableAnalyticV2Recommendation = enableAnalyticV2Recommendation(tenantApp);

    const displayedSourceDate = displayDateRangeV2(fromDate, toDate);
    const displayedComparedDate = buildCompareDateRange(
      fromDate,
      toDate,
      buildDateRangeFromDate(fromDate, toDate),
      'v2'
    );

    const { series: totalRevenueSeries, categories: totalRevenueCategories } = this.buildChartData(
      totalRevenueData,
      totalRevenueCompared,
      displayedSourceDate,
      displayedComparedDate,
      null,
      MAX_NUM_X_AXIS_POINTS_APP_IMPACT_CHART
    );
    const totalRevenueExtraData = this.buildSpecificTotalRevenue(isEnableAnalyticV2Recommendation);

    const { series: totalConversionRateSeries, categories: totalConversionRateCategories } =
      this.buildChartData(
        totalConversionRateData,
        totalConversionRateCompared,
        displayedSourceDate,
        displayedComparedDate,
        null,
        MAX_NUM_X_AXIS_POINTS_APP_IMPACT_CHART
      );
    const totalConversionRateExtraData = this.buildSpecificConversionRate(
      isEnableAnalyticV2Recommendation
    );

    const { series: orderCountSeries, categories: orderCountCategories } = this.buildChartData(
      orderCountData,
      orderCountCompared,
      displayedSourceDate,
      displayedComparedDate,
      null,
      MAX_NUM_X_AXIS_POINTS_APP_IMPACT_CHART
    );
    const orderCountExtraData = this.buildSpecificOrderCount(isEnableAnalyticV2Recommendation);

    const { series: averageOrderValueSeries, categories: averageOrderValueCategories } =
      this.buildChartData(
        averageOrderValueData,
        averageOrderValueCompared,
        displayedSourceDate,
        displayedComparedDate,
        null,
        MAX_NUM_X_AXIS_POINTS_APP_IMPACT_CHART
      );
    const averageOrderValueExtraData = this.buildSpecificAverageOrderValue(
      isEnableAnalyticV2Recommendation
    );

    const { series: revenuePerVisitorSeries, categories: revenuePerVisitorCategories } =
      this.buildChartData(
        revenuePerVisitorData,
        revenuePerVisitorCompared,
        displayedSourceDate,
        displayedComparedDate,
        null,
        MAX_NUM_X_AXIS_POINTS_APP_IMPACT_CHART
      );
    const revenuePerVisitorExtraData = this.buildSpecificRevenuePerVisitor(
      isEnableAnalyticV2Recommendation
    );

    setTimeout(() => {
      this.processHideLandMarks(orderCountCategories);
    }, 1000);

    return (
      <div className="BC-Page BC-Page__AppImpactCharts">
        <WarningMetricIncompatible />
        <Filter
          performActionSurvey={performActionSurvey}
          fromDateStateName={fromDateStateName}
          toDateStateName={toDateStateName}
          compareStateName={compareStateName}
          feature={FEATURES.APP_IMPACT}
          openReviewAddonModal={openReviewAddonModal}
        />
        <div>
          <Grid>
            {/* Total revenue generated by Boost */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsChartImpact
                tooltip={i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_revenue')}
                title={i18n.translate('AdminHTML.Analytics.AppImpact.totalRevenue_CardTitle')}
                total={formatMoney(totalRevenueTotal, moneyFormat)}
                percentGrowthChange={totalRevenuePercentChange}
                loading={totalRevenueLoading}
                compare={compare}
                chart={
                  <Chart
                    isLimitxAxis
                    className="AppImpact-Total-revenue"
                    transparent
                    type="line"
                    height="270px"
                    series={totalRevenueSeries}
                    categories={totalRevenueCategories}
                    unlimitedYAxis
                  />
                }
                extraData={totalRevenueExtraData}
              />
            </Grid.Cell>

            {/* Conversion rate  */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsChartImpact
                tooltip={i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_conversionRate')}
                title={i18n.translate('AdminHTML.Analytics.AppImpact.conversionRate_CardTitle')}
                total={formatNumberInThousand(totalConversionRateTotal)}
                percentGrowthChange={totalConversionRatePercentChange}
                loading={totalConversionRateLoading}
                compare={compare}
                chart={
                  <Chart
                    isLimitxAxis
                    className="AppImpact-Conversion-rate"
                    suffixOfValue="%"
                    transparent
                    type="line"
                    height="270px"
                    series={totalConversionRateSeries}
                    categories={totalConversionRateCategories}
                  />
                }
                extraData={totalConversionRateExtraData}
              />
            </Grid.Cell>

            {/* Order count */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsChartImpact
                tooltip={i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_orderCount')}
                title={i18n.translate('AdminHTML.Analytics.AppImpact.orderCount_CardTitle')}
                total={formatNumberInThousand(orderCountTotal)}
                percentGrowthChange={orderCountPercentChange}
                loading={orderCountLoading}
                compare={compare}
                chart={
                  <Chart
                    isLimitxAxis
                    className="AppImpact-Order-count"
                    transparent
                    type="line"
                    height="270px"
                    series={orderCountSeries}
                    categories={orderCountCategories}
                    unlimitedYAxis
                  />
                }
                extraData={orderCountExtraData}
              />
            </Grid.Cell>

            {/* Average order value */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsChartImpact
                tooltip={i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_avgOrderValue')}
                title={i18n.translate('AdminHTML.Analytics.AppImpact.averageOrderValue_CardTitle')}
                total={formatMoney(Math.round(averageOrderValueTotal), moneyFormat)}
                percentGrowthChange={averageOrderValuePercentChange}
                loading={averageOrderValueLoading}
                compare={compare}
                chart={
                  <Chart
                    isLimitxAxis
                    className="AppImpact-Avg-order-value"
                    transparent
                    type="line"
                    height="270px"
                    series={averageOrderValueSeries}
                    categories={averageOrderValueCategories}
                    unlimitedYAxis
                  />
                }
                extraData={averageOrderValueExtraData}
              />
            </Grid.Cell>

            {/* Revenue per visitor */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsChartImpact
                tooltip={i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_revenuePerVisitor')}
                title={i18n.translate('AdminHTML.Analytics.AppImpact.revenuePerVisitor_CardTitle')}
                total={formatMoney(Math.round(revenuePerVisitorTotal), moneyFormat)}
                percentGrowthChange={revenuePerVisitorPercentChange}
                loading={revenuePerVisitorLoading}
                compare={compare}
                chart={
                  <Chart
                    isLimitxAxis
                    className="AppImpact-Revenue-per-visitor"
                    transparent
                    type="line"
                    height="270px"
                    series={revenuePerVisitorSeries}
                    categories={revenuePerVisitorCategories}
                    unlimitedYAxis
                  />
                }
                extraData={revenuePerVisitorExtraData}
              />
            </Grid.Cell>
          </Grid>
        </div>
      </div>
    );
  }

  buildChartData = (
    sourceDataArr,
    comparedDataArr = [],
    displayedSourceDate,
    displayedComparedDate,
    customFieldTotal, // Priority field here to get data for showing on chart
    isLimitxAxis
  ) => {
    const { compare, timezone } = this.props;

    let categories = sourceDataArr.map(item => {
      // Parse UTC time into tenant's timezone to format datetime showed on chart
      return new Date(item.datetime).toLocaleString('en-US', { timeZone: timezone });
    });

    // Process limit x axis max 3,4 points
    if (isLimitxAxis && isLimitxAxis < categories.length) {
      categories = limitXAxisPoint(categories, MAX_NUM_X_AXIS_POINTS_APP_IMPACT_CHART);
    }

    const fullCategories = sourceDataArr.map(item => {
      // Parse UTC time into tenant's timezone to format datetime showed on chart
      return new Date(item.datetime).toLocaleString('en-US', { timeZone: timezone });
    });

    const data = sourceDataArr.map(item =>
      customFieldTotal ? item[customFieldTotal] : item.total
    );

    const series = [
      {
        name: displayedSourceDate,
        data: data,
        fullCategories
      }
    ];

    if (compare) {
      const comparedData = comparedDataArr.map(item =>
        customFieldTotal ? item[customFieldTotal] : item.total
      );
      series.push({
        name: displayedComparedDate,
        data: comparedData
      });
    }

    return {
      series,
      categories
    };
  };

  buildSpecificTotalRevenue = isEnableAnalyticV2Recommendation => {
    const {
      i18n,
      totalRevenueTotalCollection,
      totalRevenueTotalSearch,
      totalRevenueTotalRecommend,
      totalRevenuePercentChangeCollection,
      totalRevenuePercentChangeSearch,
      totalRevenuePercentChangeRecommend,
      moneyFormat
    } = this.props;

    return [
      {
        label: i18n.translate('AdminHTML.Analytics.AppImpact.totalRevenue_ByCollection'),
        tooltip: i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_revenue_Collection'),
        total: formatMoney(totalRevenueTotalCollection, moneyFormat),
        percentGrowthChange: totalRevenuePercentChangeCollection
      },
      {
        label: i18n.translate('AdminHTML.Analytics.AppImpact.totalRevenue_BySearch'),
        tooltip: i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_revenue_Search'),
        total: formatMoney(totalRevenueTotalSearch, moneyFormat),
        percentGrowthChange: totalRevenuePercentChangeSearch
      },
      isEnableAnalyticV2Recommendation
        ? {
            label: i18n.translate('AdminHTML.Analytics.AppImpact.totalRevenue_ByRecommendation'),
            tooltip: i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_revenue_Recommend'),
            total: formatMoney(totalRevenueTotalRecommend, moneyFormat),
            percentGrowthChange: totalRevenuePercentChangeRecommend
          }
        : {}
    ];
  };

  buildSpecificConversionRate = isEnableAnalyticV2Recommendation => {
    const {
      i18n,
      totalConversionRateTotalCollection,
      totalConversionRateTotalSearch,
      totalConversionRateTotalRecommend,
      totalConversionRatePercentChangeCollection,
      totalConversionRatePercentChangeSearch,
      totalConversionRatePercentChangeRecommend
    } = this.props;

    return [
      {
        label: i18n.translate('AdminHTML.Analytics.AppImpact.conversionRate_ByCollection'),
        tooltip: i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_conversionRate_Collection'),
        total: formatNumberInThousand(totalConversionRateTotalCollection),
        percentGrowthChange: totalConversionRatePercentChangeCollection
      },
      {
        label: i18n.translate('AdminHTML.Analytics.AppImpact.conversionRate_BySearch'),
        tooltip: i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_conversionRate_Search'),
        total: formatNumberInThousand(totalConversionRateTotalSearch),
        percentGrowthChange: totalConversionRatePercentChangeSearch
      },
      isEnableAnalyticV2Recommendation
        ? {
            label: i18n.translate('AdminHTML.Analytics.AppImpact.conversionRate_ByRecommendation'),
            tooltip: i18n.translate(
              'AdminHTML.Analytics.AppImpact.tooltip_conversionRate_Recommend'
            ),
            total: formatNumberInThousand(totalConversionRateTotalRecommend),
            percentGrowthChange: totalConversionRatePercentChangeRecommend
          }
        : {}
    ];
  };

  buildSpecificOrderCount = isEnableAnalyticV2Recommendation => {
    const {
      i18n,
      orderCountTotalCollection,
      orderCountTotalSearch,
      orderCountTotalRecommend,
      orderCountPercentChangeCollection,
      orderCountPercentChangeSearch,
      orderCountPercentChangeRecommend
    } = this.props;

    return [
      {
        label: i18n.translate('AdminHTML.Analytics.AppImpact.orderCount_ByCollection'),
        tooltip: i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_orderCount_Collection'),
        total: formatNumberInThousand(orderCountTotalCollection),
        percentGrowthChange: orderCountPercentChangeCollection
      },
      {
        label: i18n.translate('AdminHTML.Analytics.AppImpact.orderCount_BySearch'),
        tooltip: i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_orderCount_Search'),
        total: formatNumberInThousand(orderCountTotalSearch),
        percentGrowthChange: orderCountPercentChangeSearch
      },
      isEnableAnalyticV2Recommendation
        ? {
            label: i18n.translate('AdminHTML.Analytics.AppImpact.orderCount_ByRecommendation'),
            tooltip: i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_orderCount_Recommend'),
            total: formatNumberInThousand(orderCountTotalRecommend),
            percentGrowthChange: orderCountPercentChangeRecommend
          }
        : {}
    ];
  };

  buildSpecificAverageOrderValue = isEnableAnalyticV2Recommendation => {
    const {
      i18n,
      averageOrderValueTotalCollection,
      averageOrderValueTotalSearch,
      averageOrderValueTotalRecommend,
      averageOrderValuePercentChangeCollection,
      averageOrderValuePercentChangeSearch,
      averageOrderValuePercentChangeRecommend,
      moneyFormat
    } = this.props;

    return [
      {
        label: i18n.translate('AdminHTML.Analytics.AppImpact.averageOrderValue_ByCollection'),
        tooltip: i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_avgOrderValue_Collection'),
        total: formatMoney(Math.round(averageOrderValueTotalCollection), moneyFormat),
        percentGrowthChange: averageOrderValuePercentChangeCollection
      },
      {
        label: i18n.translate('AdminHTML.Analytics.AppImpact.averageOrderValue_BySearch'),
        tooltip: i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_avgOrderValue_Search'),
        total: formatMoney(Math.round(averageOrderValueTotalSearch), moneyFormat),
        percentGrowthChange: averageOrderValuePercentChangeSearch
      },
      isEnableAnalyticV2Recommendation
        ? {
            label: i18n.translate(
              'AdminHTML.Analytics.AppImpact.averageOrderValue_ByRecommendation'
            ),
            tooltip: i18n.translate(
              'AdminHTML.Analytics.AppImpact.tooltip_avgOrderValue_Recommend'
            ),
            total: formatMoney(Math.round(averageOrderValueTotalRecommend), moneyFormat),
            percentGrowthChange: averageOrderValuePercentChangeRecommend
          }
        : {}
    ];
  };

  buildSpecificRevenuePerVisitor = isEnableAnalyticV2Recommendation => {
    const {
      i18n,
      revenuePerVisitorTotalCollection,
      revenuePerVisitorTotalSearch,
      revenuePerVisitorTotalRecommend,
      revenuePerVisitorPercentChangeCollection,
      revenuePerVisitorPercentChangeSearch,
      revenuePerVisitorPercentChangeRecommend,
      moneyFormat
    } = this.props;

    return [
      {
        label: i18n.translate('AdminHTML.Analytics.AppImpact.revenuePerVisitor_ByCollection'),
        tooltip: i18n.translate(
          'AdminHTML.Analytics.AppImpact.tooltip_revenuePerVisitor_Collection'
        ),
        total: formatMoney(Math.round(revenuePerVisitorTotalCollection), moneyFormat),
        percentGrowthChange: revenuePerVisitorPercentChangeCollection
      },
      {
        label: i18n.translate('AdminHTML.Analytics.AppImpact.revenuePerVisitor_BySearch'),
        tooltip: i18n.translate('AdminHTML.Analytics.AppImpact.tooltip_revenuePerVisitor_Search'),
        total: formatMoney(Math.round(revenuePerVisitorTotalSearch), moneyFormat),
        percentGrowthChange: revenuePerVisitorPercentChangeSearch
      },
      isEnableAnalyticV2Recommendation
        ? {
            label: i18n.translate(
              'AdminHTML.Analytics.AppImpact.revenuePerVisitor_ByRecommendation'
            ),
            tooltip: i18n.translate(
              'AdminHTML.Analytics.AppImpact.tooltip_revenuePerVisitor_Recommend'
            ),
            total: formatMoney(Math.round(revenuePerVisitorTotalRecommend), moneyFormat),
            percentGrowthChange: revenuePerVisitorPercentChangeRecommend
          }
        : {}
    ];
  };

  fetchAll = (fromDate, toDate, compare) => {
    const {
      totalRevenueLoading,
      totalConversionRateLoading,
      orderCountLoading,
      averageOrderValueLoading,
      revenuePerVisitorLoading
    } = this.props;
    if (!totalRevenueLoading) {
      this.getTotalRevenue(fromDate, toDate, compare);
    }
    if (!totalConversionRateLoading) {
      this.getAppImpactConversionRate(fromDate, toDate, compare);
    }
    if (!orderCountLoading) {
      this.getOrderCount(fromDate, toDate, compare);
    }
    if (!averageOrderValueLoading) {
      this.getAverageOrderValue(fromDate, toDate, compare);
    }
    if (!revenuePerVisitorLoading) {
      this.getRevenuePerVisitor(fromDate, toDate, compare);
    }
  };

  getTotalRevenue = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_REVENUE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_REVENUE_COMPARED,
      executionIds
    );

    this.props.getTotalRevenue(fromDate, toDate, compare, queryExecutionId, comparedExecutionId);
  };

  getAppImpactConversionRate = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.CONVERSION_RATE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.CONVERSION_RATE_COMPARED,
      executionIds
    );

    this.props.getAppImpactConversionRate(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getOrderCount = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.ORDER_COUNT,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.ORDER_COUNT_COMPARED,
      executionIds
    );

    this.props.getOrderCount(fromDate, toDate, compare, queryExecutionId, comparedExecutionId);
  };

  getAverageOrderValue = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.AVERAGE_ORDER_VALUE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.AVERAGE_ORDER_VALUE_COMPARED,
      executionIds
    );

    this.props.getAverageOrderValue(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getRevenuePerVisitor = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.REVENUE_PER_VISITOR,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.REVENUE_PER_VISITOR_COMPARED,
      executionIds
    );

    this.props.getRevenuePerVisitor(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  // ---------------------------------------------------------
  processUpdateExecutionId = (props, nextProps) => {
    this.processTotalRevenueExecutionId(props, nextProps);
    this.processOrderCountExecutionId(props, nextProps);
    this.processAverageOrderExecutionId(props, nextProps);
    this.processRevenuePerVisitorExecutionId(props, nextProps);
    this.processConversionRateExecutionId(props, nextProps);
  };

  processTotalRevenueExecutionId = (props, nextProps) => {
    const { totalRevenueQueryExecutionId, totalRevenueComparedQueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      totalRevenueQueryExecutionId: totalRevenueQueryExecutionIdNext,
      totalRevenueComparedQueryExecutionId: totalRevenueComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(totalRevenueQueryExecutionIdNext) &&
      totalRevenueQueryExecutionId !== totalRevenueQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_REVENUE,
        executionId: totalRevenueQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(totalRevenueComparedQueryExecutionIdNext) &&
      totalRevenueComparedQueryExecutionId !== totalRevenueComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_REVENUE_COMPARED,
        executionId: totalRevenueComparedQueryExecutionIdNext
      });
    }
  };

  processConversionRateExecutionId = (props, nextProps) => {
    const { totalConversionRateQueryExecutionId, totalConversionRateComparedQueryExecutionId } =
      props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      totalConversionRateQueryExecutionId: totalConversionRateQueryExecutionIdNext,
      totalConversionRateComparedQueryExecutionId: totalConversionRateComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(totalConversionRateQueryExecutionIdNext) &&
      totalConversionRateQueryExecutionId !== totalConversionRateQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.CONVERSION_RATE,
        executionId: totalConversionRateQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(totalConversionRateComparedQueryExecutionIdNext) &&
      totalConversionRateComparedQueryExecutionId !==
        totalConversionRateComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.CONVERSION_RATE_COMPARED,
        executionId: totalConversionRateComparedQueryExecutionIdNext
      });
    }
  };

  processOrderCountExecutionId = (props, nextProps) => {
    const { orderCountQueryExecutionId, orderCountComparedQueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      orderCountQueryExecutionId: orderCountQueryExecutionIdNext,
      orderCountComparedQueryExecutionId: orderCountComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(orderCountQueryExecutionIdNext) &&
      orderCountQueryExecutionId !== orderCountQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.ORDER_COUNT,
        executionId: orderCountQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(orderCountComparedQueryExecutionIdNext) &&
      orderCountComparedQueryExecutionId !== orderCountComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.ORDER_COUNT_COMPARED,
        executionId: orderCountComparedQueryExecutionIdNext
      });
    }
  };

  processAverageOrderExecutionId = (props, nextProps) => {
    const { averageOrderValueQueryExecutionId, averageOrderValueComparedQueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      averageOrderValueQueryExecutionId: averageOrderValueQueryExecutionIdNext,
      averageOrderValueComparedQueryExecutionId: averageOrderValueComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(averageOrderValueQueryExecutionIdNext) &&
      averageOrderValueQueryExecutionId !== averageOrderValueQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.AVERAGE_ORDER_VALUE,
        executionId: averageOrderValueQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(averageOrderValueComparedQueryExecutionIdNext) &&
      averageOrderValueComparedQueryExecutionId !== averageOrderValueComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.AVERAGE_ORDER_VALUE_COMPARED,
        executionId: averageOrderValueComparedQueryExecutionIdNext
      });
    }
  };

  processRevenuePerVisitorExecutionId = (props, nextProps) => {
    const { revenuePerVisitorQueryExecutionId, revenuePerVisitorComparedQueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      revenuePerVisitorQueryExecutionId: revenuePerVisitorQueryExecutionIdNext,
      revenuePerVisitorComparedQueryExecutionId: revenuePerVisitorComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(revenuePerVisitorQueryExecutionIdNext) &&
      revenuePerVisitorQueryExecutionId !== revenuePerVisitorQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.REVENUE_PER_VISITOR,
        executionId: revenuePerVisitorQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(revenuePerVisitorComparedQueryExecutionIdNext) &&
      revenuePerVisitorComparedQueryExecutionId !== revenuePerVisitorComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.REVENUE_PER_VISITOR_COMPARED,
        executionId: revenuePerVisitorComparedQueryExecutionIdNext
      });
    }
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),
  getTotalRevenue: (from, to, compare, queryExecutionId, comparedQueryExecutionId) =>
    dispatch(
      AnalyticsActions.getTotalRevenue(
        from,
        to,
        compare,
        queryExecutionId,
        comparedQueryExecutionId,
        ownProps.i18n
      )
    ),
  getAppImpactConversionRate: (from, to, compare, queryExecutionId, comparedQueryExecutionId) =>
    dispatch(
      AnalyticsActions.getAppImpactConversionRate(
        from,
        to,
        compare,
        queryExecutionId,
        comparedQueryExecutionId,
        ownProps.i18n
      )
    ),
  getOrderCount: (from, to, compare, queryExecutionId, comparedQueryExecutionId) =>
    dispatch(
      AnalyticsActions.getOrderCount(
        from,
        to,
        compare,
        queryExecutionId,
        comparedQueryExecutionId,
        ownProps.i18n
      )
    ),
  getAverageOrderValue: (from, to, compare, queryExecutionId, comparedQueryExecutionId) =>
    dispatch(
      AnalyticsActions.getAverageOrderValue(
        from,
        to,
        compare,
        queryExecutionId,
        comparedQueryExecutionId,
        ownProps.i18n
      )
    ),
  getRevenuePerVisitor: (from, to, compare, queryExecutionId, comparedQueryExecutionId) =>
    dispatch(
      AnalyticsActions.getRevenuePerVisitor(
        from,
        to,
        compare,
        queryExecutionId,
        comparedQueryExecutionId,
        ownProps.i18n
      )
    ),
  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  return {
    moneyFormat: _.get(state.oauth, 'tenantApp.externalInfo.money_format', ''),
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),
    tenantApp,
    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', []),

    // Total revenue
    totalRevenueData: _.get(state.analyticsV2, 'totalRevenueData', []),
    totalRevenueCompared: _.get(state.analyticsV2, 'totalRevenueCompared', []),
    totalRevenueTotal: _.get(state.analyticsV2, 'totalRevenueTotal.all', 0),
    totalRevenueTotalCollection: _.get(state.analyticsV2, 'totalRevenueTotal.collection', 0),
    totalRevenueTotalSearch: _.get(state.analyticsV2, 'totalRevenueTotal.search', 0),
    totalRevenueTotalRecommend: _.get(state.analyticsV2, 'totalRevenueTotal.recommend', 0),
    totalRevenuePercentChange: _.get(state.analyticsV2, 'totalRevenuePercentChange.all', 0),
    totalRevenuePercentChangeCollection: _.get(
      state.analyticsV2,
      'totalRevenuePercentChange.collection',
      0
    ),
    totalRevenuePercentChangeSearch: _.get(
      state.analyticsV2,
      'totalRevenuePercentChange.search',
      0
    ),
    totalRevenuePercentChangeRecommend: _.get(
      state.analyticsV2,
      'totalRevenuePercentChange.recommend',
      0
    ),
    totalRevenueLoading: _.get(state.analyticsV2, 'totalRevenueLoading', false),
    totalRevenueQueryExecutionId: _.get(state.analyticsV2, 'totalRevenueQueryExecutionId', ''),
    totalRevenueComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'totalRevenueComparedQueryExecutionId',
      ''
    ),

    // Total Conversion rate
    totalConversionRateData: _.get(state.analyticsV2, 'totalConversionRateData', []),
    totalConversionRateCompared: _.get(state.analyticsV2, 'totalConversionRateCompared', []),
    totalConversionRateTotal: _.get(state.analyticsV2, 'totalConversionRateTotal.all', 0),
    totalConversionRateTotalCollection: _.get(
      state.analyticsV2,
      'totalConversionRateTotal.collection',
      0
    ),
    totalConversionRateTotalSearch: _.get(state.analyticsV2, 'totalConversionRateTotal.search', 0),
    totalConversionRateTotalRecommend: _.get(
      state.analyticsV2,
      'totalConversionRateTotal.recommend',
      0
    ),
    totalConversionRatePercentChange: _.get(
      state.analyticsV2,
      'totalConversionRatePercentChange.all',
      0
    ),
    totalConversionRatePercentChangeCollection: _.get(
      state.analyticsV2,
      'totalConversionRatePercentChange.collection',
      0
    ),
    totalConversionRatePercentChangeSearch: _.get(
      state.analyticsV2,
      'totalConversionRatePercentChange.search',
      0
    ),
    totalConversionRatePercentChangeRecommend: _.get(
      state.analyticsV2,
      'totalConversionRatePercentChange.recommend',
      0
    ),
    totalConversionRateLoading: _.get(state.analyticsV2, 'totalConversionRateLoading', false),
    totalConversionRateQueryExecutionId: _.get(
      state.analyticsV2,
      'totalConversionRateQueryExecutionId',
      ''
    ),
    totalConversionRateComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'totalConversionRateComparedQueryExecutionId',
      ''
    ),

    // Order count
    orderCountData: _.get(state.analyticsV2, 'orderCountData', []),
    orderCountCompared: _.get(state.analyticsV2, 'orderCountCompared', []),
    orderCountTotal: _.get(state.analyticsV2, 'orderCountTotal.all', 0),
    orderCountTotalCollection: _.get(state.analyticsV2, 'orderCountTotal.collection', 0),
    orderCountTotalSearch: _.get(state.analyticsV2, 'orderCountTotal.search', 0),
    orderCountTotalRecommend: _.get(state.analyticsV2, 'orderCountTotal.recommend', 0),
    orderCountPercentChange: _.get(state.analyticsV2, 'orderCountPercentChange.all', 0),
    orderCountPercentChangeCollection: _.get(
      state.analyticsV2,
      'orderCountPercentChange.collection',
      0
    ),
    orderCountPercentChangeSearch: _.get(state.analyticsV2, 'orderCountPercentChange.search', 0),
    orderCountPercentChangeRecommend: _.get(
      state.analyticsV2,
      'orderCountPercentChange.recommend',
      0
    ),
    orderCountLoading: _.get(state.analyticsV2, 'orderCountLoading', false),
    orderCountQueryExecutionId: _.get(state.analyticsV2, 'orderCountQueryExecutionId', ''),
    orderCountComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'orderCountComparedQueryExecutionId',
      ''
    ),

    // Average order value
    averageOrderValueData: _.get(state.analyticsV2, 'averageOrderValueData', []),
    averageOrderValueCompared: _.get(state.analyticsV2, 'averageOrderValueCompared', []),
    averageOrderValueTotal: _.get(state.analyticsV2, 'averageOrderValueTotal.all', 0),
    averageOrderValueTotalCollection: _.get(
      state.analyticsV2,
      'averageOrderValueTotal.collection',
      0
    ),
    averageOrderValueTotalSearch: _.get(state.analyticsV2, 'averageOrderValueTotal.search', 0),
    averageOrderValueTotalRecommend: _.get(
      state.analyticsV2,
      'averageOrderValueTotal.recommend',
      0
    ),
    averageOrderValuePercentChange: _.get(
      state.analyticsV2,
      'averageOrderValuePercentChange.all',
      0
    ),
    averageOrderValuePercentChangeCollection: _.get(
      state.analyticsV2,
      'averageOrderValuePercentChange.collection',
      0
    ),
    averageOrderValuePercentChangeSearch: _.get(
      state.analyticsV2,
      'averageOrderValuePercentChange.search',
      0
    ),
    averageOrderValuePercentChangeRecommend: _.get(
      state.analyticsV2,
      'averageOrderValuePercentChange.recommend',
      0
    ),
    averageOrderValueLoading: _.get(state.analyticsV2, 'averageOrderValueLoading', false),
    averageOrderValueQueryExecutionId: _.get(
      state.analyticsV2,
      'averageOrderValueQueryExecutionId',
      ''
    ),
    averageOrderValueComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'averageOrderValueComparedQueryExecutionId',
      ''
    ),

    // Revenue per visitor
    revenuePerVisitorData: _.get(state.analyticsV2, 'revenuePerVisitorData', []),
    revenuePerVisitorCompared: _.get(state.analyticsV2, 'revenuePerVisitorCompared', []),
    revenuePerVisitorTotal: _.get(state.analyticsV2, 'revenuePerVisitorTotal.all', 0),
    revenuePerVisitorTotalCollection: _.get(
      state.analyticsV2,
      'revenuePerVisitorTotal.collection',
      0
    ),
    revenuePerVisitorTotalSearch: _.get(state.analyticsV2, 'revenuePerVisitorTotal.search', 0),
    revenuePerVisitorTotalRecommend: _.get(
      state.analyticsV2,
      'revenuePerVisitorTotal.recommend',
      0
    ),
    revenuePerVisitorPercentChange: _.get(
      state.analyticsV2,
      'revenuePerVisitorPercentChange.all',
      0
    ),
    revenuePerVisitorPercentChangeCollection: _.get(
      state.analyticsV2,
      'revenuePerVisitorPercentChange.collection',
      0
    ),
    revenuePerVisitorPercentChangeSearch: _.get(
      state.analyticsV2,
      'revenuePerVisitorPercentChange.search',
      0
    ),
    revenuePerVisitorPercentChangeRecommend: _.get(
      state.analyticsV2,
      'revenuePerVisitorPercentChange.recommend',
      0
    ),
    revenuePerVisitorLoading: _.get(state.analyticsV2, 'revenuePerVisitorLoading', false),
    revenuePerVisitorQueryExecutionId: _.get(
      state.analyticsV2,
      'revenuePerVisitorQueryExecutionId',
      ''
    ),
    revenuePerVisitorComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'revenuePerVisitorComparedQueryExecutionId',
      ''
    )
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(AppImpactContainer));
